/**
 * Schémas Zod du module société : fiche, paramètres avancés, modules activés et numérotation.
 * Référence : document 06 § 6.2 (contrats d'entrée) et document 04 § 5.1.
 */
import { z } from 'zod';
import { NUMBERING_DOCUMENT_TYPES, VAT_REGIMES } from '../enums/platform.enum.js';
import { DOCUMENT_CHAIN_MODES } from '../constants/documents.constants.js';
import { CALENDAR_VISIBILITY_MODES, COMPANY_ACTIVITY_PROFILES, TENANT_AGENCY_MAX, TENANT_APPOINTMENT_TEMPLATE_MAX, TENANT_CRM_DIRECTORY_MAX, TENANT_WORKING_HOURS_MAX, } from '../constants/company.constants.js';
import { MAIL_TEMPLATE_KINDS, } from '../constants/mail-template.constants.js';
import { NUMBERING_MONTH_FORMATS, NUMBERING_PADDING_MAX, NUMBERING_PADDING_MIN, NUMBERING_PATTERN_TOKENS, NUMBERING_YEAR_FORMATS, } from '../constants/numbering.constants.js';
import { emailSchema, tenantSlugSchema } from './auth.schema.js';
import { paginationQuerySchema, uuidSchema } from './common.schema.js';
// --- Fiche société (PATCH /tenant) ------------------------------------------------------------
const PATTERN_TOKEN = /\{([a-z]+)\}/g;
/**
 * Valide un motif de numérotation : aucune accolade orpheline, jetons tous connus,
 * et présence obligatoire du jeton `{counter}` (sinon la numérotation serait constante).
 * Retourne le message d'erreur, ou `null` si le motif est valide.
 */
export function validateNumberingPattern(pattern) {
    const stripped = pattern.replace(new RegExp(PATTERN_TOKEN.source, 'g'), '');
    if (/[{}]/.test(stripped)) {
        return 'Motif de numérotation invalide : accolade non fermée.';
    }
    const tokens = [...pattern.matchAll(PATTERN_TOKEN)].map((match) => match[1] ?? '');
    const unknown = tokens.filter((token) => !NUMBERING_PATTERN_TOKENS.includes(token));
    if (unknown.length > 0) {
        return `Jeton de numérotation inconnu : ${[...new Set(unknown)].join(', ')}.`;
    }
    if (!tokens.includes('counter')) {
        return 'Le motif de numérotation doit contenir le jeton {counter}.';
    }
    return null;
}
/** Motif de numérotation (`{prefix}-{year}-{counter}`). */
export const numberingPatternSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .superRefine((value, ctx) => {
    const message = validateNumberingPattern(value);
    if (message !== null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    }
});
const optionalText = (max) => z.string().trim().max(max).nullable().optional();
const optionalDigits = (length) => z
    .string()
    .trim()
    .regex(new RegExp(`^\\d{${length}}$`), { message: `${length} chiffres attendus.` })
    .nullable()
    .optional();
const optionalCode = (length) => z
    .string()
    .trim()
    .length(length, { message: `${length} caractères attendus.` })
    .transform((value) => value.toUpperCase())
    .optional();
/** Corps de `PATCH /tenant` : identité légale, coordonnées et paramètres régionaux. */
export const tenantUpdateSchema = z
    .object({
    legalName: z.string().trim().min(2).max(180).optional(),
    tradeName: optionalText(180),
    legalForm: optionalText(40),
    siren: optionalDigits(9),
    siret: optionalDigits(14),
    vatNumber: optionalText(20),
    apeCode: optionalText(10),
    rcsCity: optionalText(80),
    capitalAmount: z.number().min(0).max(999_999_999_999).nullable().optional(),
    addressLine1: optionalText(180),
    addressLine2: optionalText(180),
    addressLine3: optionalText(180),
    postalCode: optionalText(12),
    city: optionalText(120),
    countryCode: optionalCode(2),
    email: z.string().trim().email().max(190).nullable().optional(),
    phone: optionalText(30),
    website: optionalText(190),
    iban: optionalText(34),
    bic: optionalText(11),
    logoFileId: uuidSchema.nullable().optional(),
    currency: optionalCode(3),
    timezone: z.string().trim().min(3).max(64).optional(),
    locale: z.string().trim().min(2).max(10).optional(),
    vatRegime: z.enum(VAT_REGIMES).optional(),
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
// --- Paramètres avancés (PATCH /tenant/settings) ----------------------------------------------
/** Sous-ensemble documentaire des paramètres avancés (`settings.documents`, document 09 § 11). */
export const tenantDocumentSettingsSchema = z.object({
    chain: z.enum(DOCUMENT_CHAIN_MODES),
});
/** Agence ou antenne déclarée dans `settings.company.agencies`. */
const tenantAgencySchema = z.object({
    id: z.string().trim().min(1).max(64),
    label: z.string().trim().min(1).max(120),
    addressLine1: z.string().trim().max(180).nullable().optional(),
    addressLine2: z.string().trim().max(180).nullable().optional(),
    postalCode: z.string().trim().max(12).nullable().optional(),
    city: z.string().trim().max(120).nullable().optional(),
    countryCode: optionalCode(2),
    phone: z.string().trim().max(30).nullable().optional(),
    email: z.string().trim().email().max(190).nullable().optional(),
    siret: optionalDigits(14),
});
/** Sous-ensemble « identité opérationnelle » des paramètres avancés (`settings.company`). */
export const tenantCompanySettingsSchema = z.object({
    insuranceNumber: z.string().trim().max(60).nullable().optional(),
    insuranceProvider: z.string().trim().max(120).nullable().optional(),
    insuranceAddress: optionalText(180),
    insuranceExpiresAt: z
        .string()
        .trim()
        .regex(/^\d{4}-\d{2}-\d{2}$/, { message: 'Date attendue au format AAAA-MM-JJ.' })
        .nullable()
        .optional(),
    activityProfiles: z.array(z.enum(COMPANY_ACTIVITY_PROFILES)).optional(),
    agencies: z.array(tenantAgencySchema).max(TENANT_AGENCY_MAX).optional(),
    crmCivilities: z.array(z.string().trim().min(1).max(60)).max(TENANT_CRM_DIRECTORY_MAX).optional(),
    crmSources: z.array(z.string().trim().min(1).max(60)).max(TENANT_CRM_DIRECTORY_MAX).optional(),
});
/** Gabarit personnalisé d'un type d'e-mail (`settings.email.templates.<type>`). */
const mailTemplateSchema = z.object({
    subject: z.string().trim().max(200).nullable().optional(),
    body: z.string().trim().max(8000).nullable().optional(),
});
/**
 * Modèle nommé de confirmation de rendez-vous (`settings.email.appointmentTemplates`).
 * Le nom est repris comme objet du message ; un identifiant stable permet de le référencer
 * depuis un rendez-vous.
 */
const appointmentTemplateSchema = z.object({
    id: z.string().trim().min(1).max(64),
    name: z.string().trim().min(1).max(200),
    body: z.string().trim().max(8000).nullable().optional(),
});
/**
 * Sous-ensemble « signature et textes des e-mails sortants » (`settings.email`).
 * Tous les champs sont facultatifs : la fusion partielle autorise un enregistrement de la seule
 * signature (écran Société) comme des seuls gabarits (écran Paramètres e-mail). Les valeurs
 * absentes sont résolues à la lecture par `resolveEmailSettings`.
 */
export const tenantEmailSettingsSchema = z.object({
    signature: z
        .object({
        enabled: z.boolean().optional(),
        senderName: z.string().trim().max(120).nullable().optional(),
        includeLogo: z.boolean().optional(),
        text: z.string().trim().max(4000).nullable().optional(),
    })
        .optional(),
    templates: z
        .object(Object.fromEntries(MAIL_TEMPLATE_KINDS.map((kind) => [kind, mailTemplateSchema.optional()])))
        .optional(),
    appointmentTemplates: z
        .array(appointmentTemplateSchema)
        .max(TENANT_APPOINTMENT_TEMPLATE_MAX)
        .optional(),
});
/** Heure locale au format `HH:MM`. */
const timeOfDaySchema = z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Heure attendue au format HH:MM.' });
/** Plage d'ouverture d'une journée de travail (`settings.calendar.workingHours`). */
const workingHoursRangeSchema = z
    .object({ start: timeOfDaySchema, end: timeOfDaySchema })
    .refine((value) => value.start < value.end, {
    message: "L'heure de fermeture doit suivre l'heure d'ouverture.",
});
/** Sous-ensemble « agenda » des paramètres avancés (`settings.calendar`). */
export const tenantCalendarSettingsSchema = z.object({
    visibility: z.enum(CALENDAR_VISIBILITY_MODES),
    teamRoleCodes: z.array(z.string().trim().min(1).max(64)).optional(),
    workingHours: z.array(workingHoursRangeSchema).max(TENANT_WORKING_HOURS_MAX).optional(),
});
/** Corps de `PATCH /tenant/settings` : fusion partielle de l'objet `settings`. */
export const tenantSettingsUpdateSchema = z.object({
    settings: z
        .record(z.string(), z.unknown())
        .refine((value) => Object.keys(value).length > 0, { message: 'Aucun paramètre fourni.' })
        .superRefine((value, ctx) => {
        const subsets = [
            { key: 'documents', schema: tenantDocumentSettingsSchema },
            { key: 'company', schema: tenantCompanySettingsSchema },
            { key: 'email', schema: tenantEmailSettingsSchema },
            { key: 'calendar', schema: tenantCalendarSettingsSchema },
        ];
        for (const { key, schema } of subsets) {
            const subset = value[key];
            if (subset === undefined) {
                continue;
            }
            if (!schema.safeParse(subset).success) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [key],
                    message: `Paramètres « ${key} » invalides.`,
                });
            }
        }
    }),
});
// --- Modules activés (PATCH /tenant/feature-flags) --------------------------------------------
/** Corps de `PATCH /tenant/feature-flags` : active ou désactive un module. */
export const tenantFeatureFlagUpdateSchema = z.object({
    featureCode: z.string().trim().min(2).max(60),
    isEnabled: z.boolean(),
    config: z.record(z.string(), z.unknown()).nullable().optional(),
});
/** Parametres de `GET /public/tenants/:slug/legal`. */
export const tenantPublicLegalParamsSchema = z.object({
    slug: tenantSlugSchema,
});
/**
 * Paramètres de `GET /tenant/bank-lookup`.
 * Seul le code banque (5 chiffres) transite : l'IBAN complet n'est jamais transmis.
 */
export const tenantBankLookupQuerySchema = z.object({
    bankCode: z
        .string()
        .trim()
        .regex(/^\d{5}$/, { message: 'Code banque à 5 chiffres attendu.' }),
});
/**
 * Corps de `POST /tenant/email/test` : adresse de destination de l'e-mail d'essai, et
 * type de gabarit à tester. Sans `kind`, l'essai envoie un message générique de validation.
 * Pour une confirmation de rendez-vous, `appointmentTemplateId` cible un modèle nommé précis.
 */
export const tenantEmailTestSchema = z.object({
    to: emailSchema,
    kind: z.enum(MAIL_TEMPLATE_KINDS).optional(),
    appointmentTemplateId: z.string().trim().min(1).max(64).optional(),
});
// --- Numérotation (document 06 § 6.2) ---------------------------------------------------------
const numberingSequenceFields = {
    pattern: numberingPatternSchema,
    prefix: optionalText(16),
    suffix: optionalText(16),
    yearFormat: z.enum(NUMBERING_YEAR_FORMATS).optional(),
    monthFormat: z.enum(NUMBERING_MONTH_FORMATS).optional(),
    padding: z.number().int().min(NUMBERING_PADDING_MIN).max(NUMBERING_PADDING_MAX).optional(),
    counterStart: z.number().int().min(1).max(1_000_000).optional(),
    resetYearly: z.boolean().optional(),
    resetMonthly: z.boolean().optional(),
    isDefault: z.boolean().optional(),
};
/**
 * Corps de `POST /settings/numbering-sequences`.
 * `fiscalYear` est facultatif : par défaut l'exercice courant. Il permet de préparer la règle
 * de l'exercice suivant, l'unicité portant sur `(document_type, exercice)`.
 */
export const numberingSequenceCreateSchema = z.object({
    documentType: z.enum(NUMBERING_DOCUMENT_TYPES),
    fiscalYear: z.number().int().min(2000).max(2999).optional(),
    pattern: numberingSequenceFields.pattern,
    prefix: numberingSequenceFields.prefix,
    suffix: numberingSequenceFields.suffix,
    yearFormat: numberingSequenceFields.yearFormat,
    monthFormat: numberingSequenceFields.monthFormat,
    padding: numberingSequenceFields.padding,
    counterStart: numberingSequenceFields.counterStart,
    resetYearly: numberingSequenceFields.resetYearly,
    resetMonthly: numberingSequenceFields.resetMonthly,
    isDefault: numberingSequenceFields.isDefault,
});
/** Corps de `PATCH /settings/numbering-sequences/:id` : `documentType` reste immuable. */
export const numberingSequenceUpdateSchema = z
    .object({
    pattern: numberingSequenceFields.pattern.optional(),
    prefix: numberingSequenceFields.prefix,
    suffix: numberingSequenceFields.suffix,
    yearFormat: numberingSequenceFields.yearFormat,
    monthFormat: numberingSequenceFields.monthFormat,
    padding: numberingSequenceFields.padding,
    counterStart: numberingSequenceFields.counterStart,
    resetYearly: numberingSequenceFields.resetYearly,
    resetMonthly: numberingSequenceFields.resetMonthly,
    isDefault: numberingSequenceFields.isDefault,
})
    .refine((value) => Object.values(value).some((field) => field !== undefined), {
    message: 'Aucun champ à mettre à jour.',
});
/** Corps de `POST /settings/numbering-sequences/preview` : aperçu sans consommation. */
export const numberingSequencePreviewSchema = z.object({
    documentType: z.enum(NUMBERING_DOCUMENT_TYPES).optional(),
    pattern: numberingSequenceFields.pattern,
    prefix: numberingSequenceFields.prefix,
    suffix: numberingSequenceFields.suffix,
    yearFormat: numberingSequenceFields.yearFormat,
    monthFormat: numberingSequenceFields.monthFormat,
    padding: numberingSequenceFields.padding,
    counterStart: numberingSequenceFields.counterStart,
    counter: z.number().int().min(0).max(999_999_999).optional(),
});
/** Paramètres de `GET /settings/numbering-sequences`. */
export const numberingSequenceListQuerySchema = paginationQuerySchema.extend({
    filter: z
        .object({
        documentType: z.enum(NUMBERING_DOCUMENT_TYPES).optional(),
        fiscalYear: z.coerce.number().int().min(2000).max(2999).optional(),
    })
        .optional(),
});
//# sourceMappingURL=tenant.schema.js.map